import Phaser from 'phaser';
import { BaseGameScene } from './BaseGameScene';
import { HEADER_HEIGHT } from '../config';
import { COLORS, TextStyles, DISPLAY_FONT, Radii } from '../ui/theme';
import { store } from '../state/store';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { measureValue, bowlFillFraction } from '../systems/MixingSystem';
import type { BaseUnit, Ingredient, Measure, SceneName } from '../types';

const UNITS: { unit: BaseUnit; label: string }[] = [
  { unit: 'tsp', label: 'Tsp' },
  { unit: 'tbsp', label: 'Tbsp' },
  { unit: 'cup', label: 'Cup' },
  { unit: 'egg', label: 'Egg' },
  { unit: 'oz', label: 'Oz' },
  { unit: 'count', label: 'Count' },
];

const FRACTIONS: { label: string; value: number }[] = [
  { label: '0', value: 0 },
  { label: '1/4', value: 0.25 },
  { label: '1/3', value: 1 / 3 },
  { label: '1/2', value: 0.5 },
  { label: '2/3', value: 2 / 3 },
  { label: '3/4', value: 0.75 },
];

const VISIBLE_TILES = 3;

// Placeholder tile art until real ingredient sprites land.
const PLACEHOLDER_EMOJI: Record<string, string> = {
  zucchini: '\u{1F952}',
  bananas: '\u{1F34C}',
};

export class KitchenScene extends BaseGameScene {
  // Recipe book
  private bookOpen = false;
  private pageIndex = 0;
  private bookArea!: { x: number; y: number; w: number; h: number };
  private bookNodes: Phaser.GameObjects.GameObject[] = [];

  // Cupboard carousel
  private cupboardStart = 0;
  private selectedIngredient: Ingredient | null = null;
  private cupboardNodes: Phaser.GameObjects.GameObject[] = [];
  private cupboardArea!: { x: number; y: number; w: number; h: number };

  // Measuring row
  private unitSpinner!: Spinner;
  private qtySpinner!: Spinner;
  private fractionSpinner!: Spinner;
  private ingredientLabel!: Phaser.GameObjects.Text;

  // Bowl + added list
  private addedText!: Phaser.GameObjects.Text;
  private bowlG!: Phaser.GameObjects.Graphics;
  private bakeBtn!: Button;
  private ovenHint!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'Kitchen' });
  }

  protected sceneName(): Extract<SceneName, 'Menu' | 'Kitchen' | 'Oven' | 'Counter'> { return 'Kitchen'; }
  protected title(): string { return ''; }
  protected backgroundColor(): number { return COLORS.bgKitchen; }

  create(): void {
    this.setupChrome();
    const w = this.scale.width;
    const h = this.scale.height;

    // Restore mid-mix state (reload / nav-away survival).
    const mixing = store.state.mixing;
    if (mixing) {
      this.bookOpen = true;
      const idx = store.content.recipes.findIndex((r) => r.id === mixing.recipe);
      this.pageIndex = Math.max(0, idx);
    }

    // ── Left column: recipe book ──────────────────────────────────────────
    this.bookArea = { x: 40, y: HEADER_HEIGHT + 30, w: 400, h: 545 };
    this.renderBook();

    // Ingredients-added panel (bottom left)
    const addedY = this.bookArea.y + this.bookArea.h + 15;
    const addedH = h - addedY - 20;
    this.panel(40, addedY, 400, addedH, 0xffffff);
    this.add.text(240, addedY + 22, 'INGREDIENTS ADDED', {
      fontFamily: DISPLAY_FONT, fontSize: '20px', fontStyle: '600', color: '#553089',
    }).setOrigin(0.5);
    this.addedText = this.add.text(64, addedY + 48, '', {
      fontFamily: DISPLAY_FONT, fontSize: '17px', color: '#2a1f4a',
      wordWrap: { width: 350 }, lineSpacing: 4,
    });
    new Button(this, 240, addedY + addedH - 32, 'Empty Bowl', {
      fill: 0xaaaaaa, hoverFill: 0xcccccc, fontSize: '18px', width: 160, height: 42,
      onClick: () => { store.emptyBowl(); this.refreshMix(); },
    });

    // ── Right column: cupboard ────────────────────────────────────────────
    this.cupboardArea = { x: 470, y: HEADER_HEIGHT + 30, w: w - 470 - 40, h: 320 };
    this.panel(this.cupboardArea.x, this.cupboardArea.y, this.cupboardArea.w, this.cupboardArea.h, 0xffffff);
    this.add.text(this.cupboardArea.x + this.cupboardArea.w / 2, this.cupboardArea.y + 26, 'YOUR CUPBOARD', {
      fontFamily: DISPLAY_FONT, fontSize: '24px', fontStyle: '600', color: '#9a7fd1',
    }).setOrigin(0.5);
    this.renderCupboard();

    // ── Measuring row ─────────────────────────────────────────────────────
    const measY = this.cupboardArea.y + this.cupboardArea.h + 40;
    const mx = this.cupboardArea.x;
    this.add.text(mx + this.cupboardArea.w / 2, measY, 'How much would you like to add?', {
      fontFamily: DISPLAY_FONT, fontSize: '26px', fontStyle: 'italic 600', color: '#9a7fd1',
    }).setOrigin(0.5);

    const rowY = measY + 60;
    this.add.text(mx + 10, rowY, 'Units:', TextStyles.LABEL).setOrigin(0, 0.5);
    this.unitSpinner = new Spinner(this, mx + 155, rowY, {
      values: UNITS.map((u) => u.label), width: 150, height: 54,
    });
    this.add.text(mx + 265, rowY, 'Qty:', TextStyles.LABEL).setOrigin(0, 0.5);
    this.qtySpinner = new Spinner(this, mx + 385, rowY, {
      values: Array.from({ length: 13 }, (_, i) => String(i)), width: 120, height: 54,
    });
    this.fractionSpinner = new Spinner(this, mx + 530, rowY, {
      values: FRACTIONS.map((f) => f.label), width: 120, height: 54,
    });

    const row2Y = rowY + 70;
    this.add.text(mx + 10, row2Y, 'Ingredient:', TextStyles.LABEL).setOrigin(0, 0.5);
    this.ingredientLabel = this.add.text(mx + 155, row2Y, '—', {
      fontFamily: DISPLAY_FONT, fontSize: '26px', fontStyle: '600', color: '#ffffff',
      backgroundColor: '#f7901e', padding: { left: 14, right: 14, top: 6, bottom: 6 },
    }).setOrigin(0, 0.5);
    new Button(this, mx + 640, row2Y, 'ADD ▸', {
      fill: COLORS.accentGreen, hoverFill: 0x5fe28b, fontSize: '22px', width: 130, height: 52,
      onClick: () => this.addIngredient(),
    });

    // ── Bowl + BAKE IT ────────────────────────────────────────────────────
    this.bowlG = this.add.graphics();
    this.bowlG.setPosition(mx + this.cupboardArea.w / 2 - 60, h - 155);

    this.bakeBtn = new Button(this, w - 165, h - 70, 'BAKE IT!!', {
      fill: COLORS.accentOrange, hoverFill: 0xffb24a, fontSize: '26px', width: 210, height: 62,
      onClick: () => this.bakeIt(),
    });
    this.ovenHint = this.add.text(w - 165, h - 118, '', {
      fontFamily: DISPLAY_FONT, fontSize: '16px', fontStyle: 'italic', color: '#c23b2f',
    }).setOrigin(0.5);

    this.refreshMix();
  }

  // ── Recipe book ───────────────────────────────────────────────────────────

  private clearBook(): void {
    this.bookNodes.forEach((n) => n.destroy());
    this.bookNodes = [];
  }

  private renderBook(): void {
    this.clearBook();
    const { x, y, w, h } = this.bookArea;
    const track = <T extends Phaser.GameObjects.GameObject>(n: T): T => {
      this.bookNodes.push(n);
      return n;
    };

    if (!this.bookOpen) {
      // Closed cover
      const g = track(this.add.graphics());
      g.fillStyle(COLORS.headerPurple, 1);
      g.fillRoundedRect(x, y, w, h, Radii.lg);
      g.lineStyle(4, COLORS.headerPurpleDeep, 1);
      g.strokeRoundedRect(x, y, w, h, Radii.lg);
      // Spiral rings across the top
      g.lineStyle(6, COLORS.headerPurpleDeep, 1);
      for (let i = 0; i < 8; i++) {
        const rx = x + 40 + i * ((w - 80) / 7);
        g.strokeCircle(rx, y + 8, 14);
      }
      track(this.add.text(x + w / 2, y + h / 2 - 30, 'RECIPE\nBOOK', {
        fontFamily: DISPLAY_FONT, fontSize: '44px', fontStyle: '700',
        color: '#ffe6b3', align: 'center',
      }).setOrigin(0.5));
      track(this.add.text(x + w / 2, y + h / 2 + 70, 'click to open', {
        fontFamily: DISPLAY_FONT, fontSize: '20px', fontStyle: 'italic', color: '#d9c8f5',
      }).setOrigin(0.5));

      const hit = track(this.add.zone(x + w / 2, y + h / 2, w, h));
      (hit as Phaser.GameObjects.Zone).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => { this.bookOpen = true; this.renderBook(); });
      return;
    }

    // Open page
    const recipes = store.content.recipes;
    const recipe = recipes[this.pageIndex];
    const g = track(this.add.graphics());
    g.fillStyle(0xfffbe6, 1);
    g.fillRoundedRect(x, y, w, h, Radii.md);
    g.lineStyle(4, COLORS.headerPurple, 1);
    g.strokeRoundedRect(x, y, w, h, Radii.md);
    g.lineStyle(6, COLORS.headerPurpleDeep, 1);
    for (let i = 0; i < 8; i++) {
      const rx = x + 40 + i * ((w - 80) / 7);
      g.strokeCircle(rx, y + 4, 12);
    }

    track(this.add.text(x + w / 2, y + 52, recipe.name, {
      fontFamily: DISPLAY_FONT, fontSize: '30px', fontStyle: '700', color: '#7a4fbe',
      align: 'center', wordWrap: { width: w - 40 },
    }).setOrigin(0.5));
    track(this.add.text(x + w / 2, y + 92, `Makes ${recipe.yield}`, {
      fontFamily: DISPLAY_FONT, fontSize: '20px', fontStyle: 'italic 600', color: '#553089',
    }).setOrigin(0.5));

    let ly = y + 128;
    for (const r of recipe.ingredients) {
      const ing = store.content.ingredients.find((i) => i.id === r.ingredient);
      track(this.add.text(x + 36, ly, `${formatMeasure(r.measure)} ${ing?.name ?? r.ingredient}`, {
        fontFamily: DISPLAY_FONT, fontSize: '19px', color: '#3d2c73',
      }));
      ly += 30;
    }
    track(this.add.text(x + w / 2, y + h - 128, `Bake At ${recipe.tempF}°F For ${recipe.minutes} Mins`, {
      fontFamily: DISPLAY_FONT, fontSize: '19px', fontStyle: 'italic', color: '#9a7fd1',
    }).setOrigin(0.5));

    // Page arrows
    const mkArrow = (ax: number, dir: -1 | 1, label: string) => {
      const t = track(this.add.text(ax, y + h - 40, label, {
        fontFamily: DISPLAY_FONT, fontSize: '34px', fontStyle: '700', color: '#ea4691',
      }).setOrigin(0.5));
      t.setInteractive({ useHandCursor: true });
      t.on('pointerover', () => t.setScale(1.15));
      t.on('pointerout', () => t.setScale(1));
      t.on('pointerup', () => {
        this.pageIndex = (this.pageIndex + dir + recipes.length) % recipes.length;
        this.renderBook();
      });
    };
    mkArrow(x + 40, -1, '◀');
    mkArrow(x + w - 40, +1, '▶');

    // Make This! — or status when this page's recipe is the one mixing.
    const mixing = store.state.mixing;
    if (mixing && mixing.recipe === recipe.id) {
      track(this.add.text(x + w / 2, y + h - 78, '✓ in the bowl', {
        fontFamily: DISPLAY_FONT, fontSize: '22px', fontStyle: '600', color: '#1f8b3a',
      }).setOrigin(0.5));
    } else {
      const btn = new Button(this, x + w / 2, y + h - 78, 'Make This!', {
        fill: COLORS.accentPink, hoverFill: 0xf76aac, fontSize: '22px', width: 190, height: 50,
        onClick: () => {
          store.chooseRecipe(recipe.id);
          this.renderBook();
          this.refreshMix();
        },
      });
      track(btn);
      if (mixing) {
        track(this.add.text(x + w / 2, y + h - 44, 'starts a new batch — current bowl is tossed', {
          fontFamily: DISPLAY_FONT, fontSize: '13px', fontStyle: 'italic', color: '#b06a6a',
        }).setOrigin(0.5));
      }
    }
  }

  // ── Cupboard ──────────────────────────────────────────────────────────────

  private clearCupboard(): void {
    this.cupboardNodes.forEach((n) => n.destroy());
    this.cupboardNodes = [];
  }

  private renderCupboard(): void {
    this.clearCupboard();
    const { x, y, w } = this.cupboardArea;
    const track = <T extends Phaser.GameObjects.GameObject>(n: T): T => {
      this.cupboardNodes.push(n);
      return n;
    };

    const ingredients = store.content.ingredients;
    const tileW = 190;
    const tileH = 215;
    const tilesY = y + 58;
    const arrowPad = 56;
    const groupW = VISIBLE_TILES * tileW + (VISIBLE_TILES - 1) * 16;
    const groupX = x + (w - groupW) / 2;

    for (let v = 0; v < VISIBLE_TILES; v++) {
      const ing = ingredients[(this.cupboardStart + v) % ingredients.length];
      const tx = groupX + v * (tileW + 16) + tileW / 2;
      const ty = tilesY + tileH / 2;
      const c = track(this.add.container(tx, ty));
      const selected = this.selectedIngredient?.id === ing.id;

      const g = this.add.graphics();
      g.fillStyle(selected ? 0xffe8d1 : 0xf2f6ff, 1);
      g.fillRoundedRect(-tileW / 2, -tileH / 2, tileW, tileH, Radii.md);
      g.lineStyle(selected ? 5 : 2, selected ? COLORS.accentOrange : COLORS.headerPurple, 1);
      g.strokeRoundedRect(-tileW / 2, -tileH / 2, tileW, tileH, Radii.md);
      c.add(g);

      if (this.textures.exists(ing.sprite)) {
        const icon = this.add.image(0, -20, ing.sprite);
        const target = 130;
        icon.setScale(Math.min(target / icon.width, target / icon.height));
        c.add(icon);
      } else {
        // Placeholder until the kids draw the art: emoji if we have one,
        // otherwise the ingredient's initial in a colored circle.
        const emoji = PLACEHOLDER_EMOJI[ing.id];
        if (emoji) {
          c.add(this.add.text(0, -20, emoji, { fontSize: '76px' }).setOrigin(0.5));
        } else {
          const ph = this.add.graphics();
          ph.fillStyle(0xd9c8f5, 1);
          ph.fillCircle(0, -20, 46);
          ph.lineStyle(3, COLORS.headerPurple, 1);
          ph.strokeCircle(0, -20, 46);
          c.add(ph);
          c.add(this.add.text(0, -20, ing.name[0] ?? '?', {
            fontFamily: DISPLAY_FONT, fontSize: '44px', fontStyle: '700', color: '#553089',
          }).setOrigin(0.5));
        }
      }
      c.add(this.add.text(0, tileH / 2 - 26, ing.name, {
        fontFamily: DISPLAY_FONT, fontSize: '18px', fontStyle: '500', color: '#2a1f4a',
      }).setOrigin(0.5));

      c.setInteractive(
        new Phaser.Geom.Rectangle(-tileW / 2, -tileH / 2, tileW, tileH),
        Phaser.Geom.Rectangle.Contains,
      );
      c.input!.cursor = 'pointer';
      c.on('pointerover', () => c.setScale(1.03));
      c.on('pointerout', () => c.setScale(1));
      c.on('pointerup', () => this.selectIngredient(ing));
    }

    // Scroll arrows
    const mkArrow = (ax: number, dir: -1 | 1, label: string) => {
      const t = track(this.add.text(ax, tilesY + tileH / 2, label, {
        fontFamily: DISPLAY_FONT, fontSize: '44px', fontStyle: '700', color: '#7fbf9e',
      }).setOrigin(0.5));
      t.setInteractive({ useHandCursor: true });
      t.on('pointerover', () => t.setScale(1.15));
      t.on('pointerout', () => t.setScale(1));
      t.on('pointerup', () => {
        const n = ingredients.length;
        // Page a full shelf at a time.
        this.cupboardStart = ((this.cupboardStart + dir * VISIBLE_TILES) % n + n) % n;
        this.renderCupboard();
      });
    };
    mkArrow(x + arrowPad / 2 + 4, -1, '◀');
    mkArrow(x + w - arrowPad / 2 - 4, +1, '▶');
  }

  private selectIngredient(ing: Ingredient): void {
    this.selectedIngredient = ing;
    this.ingredientLabel.setText(ing.name.toLowerCase());
    const unitIdx = UNITS.findIndex((u) => u.unit === ing.baseUnit);
    if (unitIdx >= 0) this.unitSpinner.setIndex(unitIdx);
    this.renderCupboard();
  }

  // ── Mixing ────────────────────────────────────────────────────────────────

  private addIngredient(): void {
    const mixing = store.state.mixing;
    if (!mixing) {
      this.cameras.main.shake(140, 0.004);
      this.ovenHint.setText('Choose a recipe first!');
      return;
    }
    if (!this.selectedIngredient) {
      this.cameras.main.shake(140, 0.004);
      return;
    }
    const qty = this.qtySpinner.index + FRACTIONS[this.fractionSpinner.index].value;
    if (qty <= 0) {
      this.cameras.main.shake(140, 0.004);
      return;
    }
    const unit = UNITS[this.unitSpinner.index].unit;
    store.addToBowl(this.selectedIngredient.id, qty, unit);
    this.qtySpinner.setIndex(0);
    this.fractionSpinner.setIndex(0);
    this.refreshMix();
  }

  private refreshMix(): void {
    const mixing = store.state.mixing;

    // Added list
    if (!mixing || mixing.adds.length === 0) {
      this.addedText.setText(mixing ? '(nothing yet — measure something!)' : '(pick a recipe from the book)');
    } else {
      const lines = mixing.adds.map((a, i) => {
        const ing = store.content.ingredients.find((x) => x.id === a.ingredient);
        const unit = a.unit === 'egg' || a.unit === 'count' ? '' : ` ${a.unit}`;
        return `${i + 1}.  ${formatQty(a.qty)}${unit} ${ing?.name.toLowerCase() ?? a.ingredient}`;
      });
      this.addedText.setText(lines.join('\n'));
    }

    // Bowl fill
    this.drawBowl();

    // BAKE IT availability + hint. Rack availability is handled at the oven
    // (the pan is carried over); the only gate here is one-pan-in-hand.
    const handsFull = store.state.carriedBake !== null;
    const canBake = !!mixing && mixing.adds.length > 0 && !handsFull;
    this.bakeBtn.setEnabled(canBake);
    if (mixing && mixing.adds.length > 0 && handsFull) {
      this.ovenHint.setText('Hands full! Put that pan in the oven first.');
    } else {
      this.ovenHint.setText('');
    }
  }

  private drawBowl(): void {
    const g = this.bowlG;
    g.clear();
    const R = 130;

    // Batter (draw first, bowl rim overlaps it)
    const mixing = store.state.mixing;
    const recipe = mixing ? store.recipeById(mixing.recipe) : undefined;
    const fill = mixing && recipe ? bowlFillFraction(mixing, recipe) : 0;
    if (fill > 0) {
      const innerR = R - 10;
      const yLevel = innerR * (1 - 0.92 * fill);
      const theta = Math.asin(Math.min(1, yLevel / innerR));
      g.fillStyle(0xf5d76e, 1);
      g.beginPath();
      g.arc(0, 0, innerR, theta, Math.PI - theta, false);
      g.closePath();
      g.fillPath();
    }

    // Bowl body: bottom semicircle
    g.lineStyle(9, COLORS.ink, 1);
    g.beginPath();
    g.arc(0, 0, R, 0, Math.PI, false);
    g.strokePath();
    // Rim
    g.lineBetween(-R - 18, 0, R + 18, 0);
    // Foot
    g.lineStyle(9, COLORS.ink, 1);
    g.strokeRoundedRect(-34, R - 4, 68, 26, 6);
  }

  private bakeIt(): void {
    const bake = store.bakeIt();
    if (!bake) {
      this.cameras.main.shake(160, 0.005);
      this.refreshMix();
      return;
    }
    this.scene.start('Oven');
  }
}

// ── formatting helpers ──────────────────────────────────────────────────────

function formatMeasure(m: Measure): string {
  return `${formatQty(measureValue(m))} ${m.unit === 'egg' || m.unit === 'count' ? '' : m.unit}`.trim();
}

function formatQty(v: number): string {
  const whole = Math.floor(v + 1e-9);
  const frac = v - whole;
  const match = [
    { label: '1/4', value: 0.25 },
    { label: '1/3', value: 1 / 3 },
    { label: '1/2', value: 0.5 },
    { label: '2/3', value: 2 / 3 },
    { label: '3/4', value: 0.75 },
  ].find((f) => Math.abs(frac - f.value) < 0.01);
  if (match) return whole > 0 ? `${whole} ${match.label}` : match.label;
  if (frac > 0.01) return v.toFixed(2);
  return String(whole);
}
